/**
 * WelcomeToPackPopup — one-time post-signup welcome modal.
 *
 * Trigger:
 *   Renders when ALL of these are true:
 *     - `?welcome=1` is present in the URL search params
 *     - The current role's profile has `welcome_popup_seen_at === null`
 *       (falsy — server-side flip is authoritative)
 *     - `user.is_impersonating` is false (never shown to an admin
 *       impersonating the target account)
 *   Otherwise renders `null`.
 *
 * Dismissal (three paths — ALL flip the server-side flag):
 *   1. `×` close button.
 *   2. "Maybe later" dismiss link.
 *   3. Clicking either social link (Facebook / Instagram opens in a
 *      new tab via `target="_blank" rel="noopener noreferrer"` — the
 *      user doesn't lose their place, but the flag flips because
 *      they've engaged with the popup).
 *
 * Persistence:
 *   Every dismissal path PATCHes the role's profile endpoint
 *   (`/profiles/customer` or `/profiles/provider`) with
 *   `{ welcome_popup_seen_at: <ISO now> }` and updates the local
 *   AuthContext state. No localStorage — server-side is authoritative
 *   so the popup does not re-appear on a different device.
 *
 * URL cleanup:
 *   After ANY dismissal, `history.replaceState` removes `?welcome=1`
 *   from the URL without pushing a history entry.
 *
 * Social links: sourced from `content/social.js` `SOCIAL_LINKS`
 *   so this stays in lockstep with the site-wide social config.
 */
import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Facebook, Instagram, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SOCIAL_LINKS } from "@/content/social";

const ICON_MAP = { Facebook, Instagram };

export default function WelcomeToPackPopup({ role }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, customerProfile, providerProfile, setCustomerProfile, setProviderProfile } = useAuth();
  const [busy, setBusy] = useState(false);
  // Local dismissal flag so the dialog closes IMMEDIATELY on click
  // without waiting for the PATCH round-trip.
  const [dismissed, setDismissed] = useState(false);

  const isCustomer = role === "customer";
  const profile = isCustomer ? customerProfile : providerProfile;
  const setProfile = isCustomer ? setCustomerProfile : setProviderProfile;

  const seen = !!profile?.welcome_popup_seen_at;
  const impersonating = !!user?.is_impersonating;
  const welcomeParam = searchParams.get("welcome");

  const shouldShow =
    welcomeParam === "1" && !seen && !impersonating && !dismissed && !!profile;

  const clearUrlParam = () => {
    // Strip ?welcome=1 without pushing a new history entry.
    const next = new URLSearchParams(searchParams);
    next.delete("welcome");
    setSearchParams(next, { replace: true });
  };

  const flipServerFlag = async () => {
    if (busy || seen) return;
    setBusy(true);
    try {
      const endpoint = isCustomer ? "/profiles/customer" : "/profiles/provider";
      const nowIso = new Date().toISOString();
      const { data } = await api.patch(endpoint, {
        welcome_popup_seen_at: nowIso,
      });
      // Server returns the updated profile — sync local state so a
      // re-mount doesn't re-fire the popup even before the next
      // /auth/me refresh.
      if (data) setProfile(data);
    } catch (e) {
      // Silent — user has visibly dismissed. Worst-case the popup
      // reappears once (server flag never got set), never a blocker.
    } finally {
      setBusy(false);
    }
  };

  const dismiss = async () => {
    // Close the dialog first (setDismissed → shouldShow=false), then
    // flip the flag + strip the URL param. Order matters so the user
    // sees an instant response even if the PATCH is slow.
    setDismissed(true);
    clearUrlParam();
    await flipServerFlag();
  };

  const handleSocialClick = async () => {
    // Social link opens in a new tab (native target="_blank"). This
    // handler ONLY handles the flag flip + URL cleanup + local close.
    // Do NOT preventDefault — we want the browser to open the tab.
    setDismissed(true);
    clearUrlParam();
    await flipServerFlag();
  };

  const handleOpenChange = (open) => {
    if (!open) {
      // Radix triggers this on ✕, Esc, or outside-click. Treat any
      // of those as a dismissal (spec Path 1).
      dismiss();
    }
  };

  if (!shouldShow) return null;

  return (
    <Dialog open={true} onOpenChange={handleOpenChange}>
      <DialogContent
        className="rounded-3xl max-w-md text-center"
        data-testid="welcome-to-pack-popup"
      >
        <DialogHeader>
          <div
            className="mx-auto mb-3 text-4xl"
            aria-hidden="true"
            data-testid="welcome-popup-paw"
          >
            🐾
          </div>
          <DialogTitle
            className="text-center font-heading text-2xl font-bold text-pp-text"
            data-testid="welcome-popup-title"
          >
            Welcome to the pack!
          </DialogTitle>
          <DialogDescription
            className="text-center text-muted-foreground"
            data-testid="welcome-popup-copy"
          >
            Follow PooPluckers for clean-yard tips, happy dog photos, and first dibs on promos.
          </DialogDescription>
        </DialogHeader>

        <div
          className="mt-5 flex flex-wrap items-center justify-center gap-3"
          data-testid="welcome-popup-social-row"
        >
          {SOCIAL_LINKS.map((link) => {
            const Icon = ICON_MAP[link.iconName];
            if (!Icon) return null;
            return (
              <a
                key={link.platform}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleSocialClick}
                aria-label={link.ariaLabel}
                data-testid={`welcome-popup-social-${link.platform.toLowerCase()}`}
                className="inline-flex items-center gap-2 rounded-full border-2 border-pp-slate px-5 py-2.5 text-[15px] font-semibold text-pp-slate transition-colors hover:border-pp-orange hover:text-pp-orange focus-visible:border-pp-orange focus-visible:text-pp-orange"
              >
                <Icon className="h-5 w-5" aria-hidden="true" />
                <span>{link.platform}</span>
              </a>
            );
          })}
        </div>

        <div className="mt-6 flex flex-col items-center gap-2">
          <button
            type="button"
            onClick={dismiss}
            className="text-sm font-semibold text-muted-foreground underline-offset-4 hover:text-pp-text hover:underline"
            data-testid="welcome-popup-maybe-later"
          >
            Maybe later
          </button>
        </div>

        {/* Explicit close button — supplements the default Radix ✕ so the
            data-testid is stable + always present in the DOM tree. */}
        <button
          type="button"
          onClick={dismiss}
          aria-label="Close welcome popup"
          data-testid="welcome-popup-close"
          className="absolute right-4 top-4 rounded-full p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-pp-text"
        >
          <X className="h-4 w-4" />
        </button>
      </DialogContent>
    </Dialog>
  );
}
